import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  CalendarCheck, 
  Target, 
  Users, 
  Shield, 
  Zap,
  Heart,
  ArrowLeft,
  CheckCircle
} from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Mission-driven",
      description: "We help individuals and businesses stop losing time, money, and opportunities to deadlines that quietly slipped by."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "User-first",
      description: "Every feature starts with a real customer conversation. Your feedback shapes the roadmap — directly."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Privacy by default",
      description: "Your data belongs to you. Industry-standard encryption, zero data sales, full export on request."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Simple wins",
      description: "Hard problems deserve clear answers. We obsess over making renewal tracking feel effortless."
    }
  ];

  const stats = [
    { value: "2,000+", label: "Active users" },
    { value: "15,000+", label: "Renewals tracked" },
    { value: "99.9%", label: "Uptime" },
    { value: "4.9/5", label: "User rating" }
  ];

  return (
    <div className="min-h-screen bg-paper">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-ink-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <CalendarCheck className="w-7 h-7 text-ink" />
              <span className="text-xl font-bold text-ink font-display">Enota</span>
            </Link>
            <Link to="/" className="text-ink-600 hover:text-ink flex items-center gap-1 text-sm">
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-white border-b border-ink-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-black text-ink tracking-tight font-display mb-6">
            About Enota
          </h1>
          <p className="text-xl text-ink-600 leading-relaxed max-w-3xl mx-auto">
            Enota is a service of <strong>OFFER GADGET LLC</strong>. We built it because no one should lose a license,
            blow an insurance deadline, or eat a late fee on a subscription they meant to cancel — just because life got busy.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-semibold text-respondent uppercase tracking-[0.2em] mb-3">Our story</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-ink tracking-tight font-display mb-6">
                Born from a missed deadline.
              </h2>
              <div className="space-y-4 text-ink-600 leading-relaxed">
                <p>
                  In 2023, our founder missed a passport renewal by three days. The result: a cancelled international
                  business trip and several thousand dollars walking out the door.
                </p>
                <p>
                  That triggered a simple question. Why is there no purpose-built tool for the renewals that actually matter?
                  Spreadsheets get ignored. Calendar pings get dismissed. Sticky notes get lost in the laundry.
                </p>
                <p>
                  Enota is the answer — a quiet, focused platform that takes deadlines seriously, so you can stop
                  carrying them around in your head.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=800" 
                alt="Team collaboration" 
                className="rounded-md shadow-xl w-full object-cover max-h-[400px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl font-bold text-white mb-2">{stat.value}</p>
                <p className="text-paper/70 text-sm uppercase tracking-wide">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold text-respondent uppercase tracking-[0.2em] mb-3">What we stand for</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-ink tracking-tight font-display">
              Four small principles. Big impact.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="bg-paper border border-ink-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-md bg-cream flex items-center justify-center text-ink mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-ink mb-2 font-display">{value.title}</h3>
                  <p className="text-ink-600 text-sm leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We Track */}
      <section className="py-16 md:py-24 bg-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-ink tracking-tight font-display mb-4">
              What our members track
            </h2>
            <p className="text-ink-600 max-w-2xl mx-auto">
              From personal documents to business contracts, if it expires we'll watch it.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Personal Documents",
                items: ["Passports & Visas", "Driver's Licenses", "ID Cards", "Professional Certifications", "Vehicle Registrations"]
              },
              {
                title: "Subscriptions",
                items: ["Software Licenses", "Streaming Services", "Gym Memberships", "Magazine Subscriptions", "Domain Names"]
              },
              {
                title: "Insurance & Contracts",
                items: ["Health Insurance", "Auto Insurance", "Home Insurance", "Life Insurance", "Lease Agreements"]
              }
            ].map((category, index) => (
              <Card key={index} className="bg-white border border-ink-200 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-ink mb-4 font-display">{category.title}</h3>
                  <ul className="space-y-2">
                    {category.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-ink-600 text-sm">
                        <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-white border-t border-ink-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-12 h-12 text-respondent mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-ink tracking-tight font-display mb-4">
            Get organized in under a minute.
          </h2>
          <p className="text-lg text-ink-600 mb-8 max-w-2xl mx-auto">
            Add your first renewal, set your reminders, and let us watch the clock from here.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-ink hover:bg-black text-white px-8">
              Get started — free
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-ink text-ink-600/70 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <CalendarCheck className="w-6 h-6 text-white" />
              <span className="text-lg font-bold text-white font-display">Enota</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/california-privacy" className="hover:text-white transition-colors">California Residents</Link>
              <Link to="/login" className="hover:text-white transition-colors">Login</Link>
            </div>
            <p className="text-sm">&copy; 2026 Enota. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
